import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-taupe text-white/50 py-8 text-center text-sm" data-id="element-43">
        <p className="mb-3" data-id="element-44">
          © {new Date().getFullYear()} El Protocolo de los 15 Minutos. Todos
          los derechos reservados.
        </p>
        <div className="flex justify-center gap-6" data-id="element-45">
          <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 hover:text-white/80 transition-colors" data-id="element-46">
            Política de Privacidad
          </button>
          <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 hover:text-white/80 transition-colors" data-id="element-47">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      <AnimatePresence data-id="element-48">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-49">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-50">
              <div className="flex items-center justify-between p-6 border-b border-taupe/10" data-id="element-51">
                <h3 className="font-serif text-2xl text-taupe" data-id="element-52">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-taupe/50 hover:text-taupe transition-colors p-1" data-id="element-53">
                  <XIcon className="w-6 h-6" data-id="element-54" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-taupe/80 leading-relaxed space-y-4 text-sm" data-id="element-55">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-56" /> : <TermsContent data-id="element-57" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-taupe/50" data-id="element-58">Última actualización: abril 2026</p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-59">
        1. Información que recopilamos
      </h4>
      <p data-id="element-60">
        Al realizar una compra, recopilamos únicamente la información necesaria
        para procesar tu pedido: nombre, dirección de correo electrónico y datos
        de pago. No almacenamos información de tarjetas de crédito; los pagos
        son procesados de forma segura por nuestro proveedor de pagos externo.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-61">
        2. Uso de la información
      </h4>
      <p data-id="element-62">Utilizamos tu información exclusivamente para:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-63">
        <li data-id="element-64">Entregar el producto digital que adquiriste.</li>
        <li data-id="element-65">Enviarte el enlace de descarga y confirmación de compra.</li>
        <li data-id="element-66">Comunicarnos contigo en caso de problemas con tu pedido.</li>
      </ul>

      <h4 className="font-serif text-lg text-taupe" data-id="element-67">
        3. Compartir información
      </h4>
      <p data-id="element-68">
        No vendemos, alquilamos ni compartimos tu información personal con
        terceros con fines de marketing. Solo compartimos datos con proveedores
        de servicios necesarios para completar tu transacción (procesador de
        pagos, servicio de correo electrónico).
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-69">4. Cookies</h4>
      <p data-id="element-70">
        Nuestro sitio puede utilizar cookies esenciales para el funcionamiento
        básico de la página y cookies analíticas para entender cómo los
        visitantes interactúan con el sitio. Puedes desactivar las cookies en la
        configuración de tu navegador.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-71">5. Seguridad</h4>
      <p data-id="element-72">
        Implementamos medidas de seguridad razonables para proteger tu
        información personal. Sin embargo, ningún método de transmisión por
        Internet es 100% seguro.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-73">6. Tus derechos</h4>
      <p data-id="element-74">
        Puedes solicitar acceso, corrección o eliminación de tus datos
        personales en cualquier momento escribiéndonos por correo electrónico.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-75">
        7. Cambios a esta política
      </h4>
      <p data-id="element-76">
        Nos reservamos el derecho de actualizar esta política en cualquier
        momento. Los cambios serán publicados en esta misma página.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-taupe/50" data-id="element-77">Última actualización: abril 2026</p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-78">1. Producto digital</h4>
      <p data-id="element-79">
        "El Protocolo de los 15 Minutos" es un producto digital en formato PDF
        que se entrega mediante descarga inmediata tras la confirmación del
        pago. No es un servicio médico, terapéutico ni sustituye la consulta con
        un profesional de la salud.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-80">2. Uso personal</h4>
      <p data-id="element-81">
        La guía es para uso personal y familiar del comprador. Queda prohibida
        su reproducción, distribución, reventa o publicación parcial o total sin
        autorización expresa por escrito.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-82">3. Resultados</h4>
      <p data-id="element-83">
        Los resultados descritos en esta página son orientativos y se basan en
        la experiencia general. Cada bebé es diferente y los resultados pueden
        variar. No garantizamos resultados específicos.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-84">
        4. Política de reembolso
      </h4>
      <p data-id="element-85">
        Debido a la naturaleza digital del producto y su entrega inmediata, no
        ofrecemos reembolsos una vez que el archivo ha sido descargado. Si
        tienes problemas técnicos para acceder al producto, contáctanos y te
        ayudaremos a resolverlo.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-86">
        5. Descargo de responsabilidad
      </h4>
      <p data-id="element-87">
        La información contenida en esta guía tiene fines educativos e
        informativos. No reemplaza el consejo médico profesional. Si tu bebé
        tiene problemas de salud o sueño persistentes, consulta con tu pediatra.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-88">
        6. Propiedad intelectual
      </h4>
      <p data-id="element-89">
        Todo el contenido, incluyendo textos, gráficos, diseños y metodología,
        está protegido por derechos de autor. El uso no autorizado puede dar
        lugar a acciones legales.
      </p>

      <h4 className="font-serif text-lg text-taupe" data-id="element-90">7. Modificaciones</h4>
      <p data-id="element-91">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. El uso continuado del sitio después de cualquier cambio
        constituye la aceptación de los nuevos términos.
      </p>
    </>;
}